import { useEffect, useState } from "react";
import { Gauge, TrendingUp, Sparkles, ArrowUpRight, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

const TONE = { emerald: "bg-emerald-500", green: "bg-green-500", slate: "bg-slate-400", amber: "bg-amber-500", red: "bg-red-500" };
const CHIP = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700", green: "border-green-200 bg-green-50 text-green-700",
  slate: "border-slate-200 bg-slate-50 text-slate-600", amber: "border-amber-200 bg-amber-50 text-amber-700",
  red: "border-red-200 bg-red-50 text-red-700",
};

export function MyImpactCard({ companyId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/portal/companies/${companyId}/impact`).then(({ data }) => setData(data)).catch(() => setErr(true));
  }, [companyId]);

  if (err) return null;
  if (!data) return <Card className="mb-8 rounded-xl border p-6"><div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading your impact…</div></Card>;

  return (
    <Card className="mb-8 rounded-xl border bg-card p-6" data-testid="my-impact-card">
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="lg:w-64 lg:shrink-0">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground"><Gauge className="h-4 w-4 text-primary" /> Your impact with {data.company_name}</div>
          <p className="mt-0.5 text-xs text-muted-foreground">Estimated · weighted for their {data.company_stage} stage</p>
          <div className="mt-4 flex items-end gap-2">
            <span className="text-5xl font-bold text-foreground" data-testid="my-impact-score">{data.score}</span>
            <span className="pb-1 text-sm text-muted-foreground">/ 100</span>
            <Badge variant="outline" className={`mb-1 ml-1 ${CHIP[data.band.tone]}`} data-testid="my-impact-band">{data.band.label}</Badge>
          </div>
          <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-muted">
            <div className={`h-full rounded-full transition-all ${TONE[data.band.tone]}`} style={{ width: `${data.score}%` }} />
          </div>
          {data.projected_effects.length > 0 && (
            <div className="mt-3 space-y-1">
              {data.projected_effects.map((e, i) => (
                <div key={i} className="flex items-center gap-1.5 text-[11px] text-emerald-700"><TrendingUp className="h-3 w-3" /> {e.text}</div>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 border-t pt-4 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
          <div className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-foreground"><Sparkles className="h-4 w-4 text-primary" /> How you can help - and raise your score</div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {data.suggestions.map((s, i) => (
              <div key={i} className="rounded-lg border p-3" data-testid={`my-impact-suggestion-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs font-medium text-foreground">{s.label}</span>
                  <span className="flex shrink-0 items-center gap-0.5 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700"><ArrowUpRight className="h-3 w-3" />+{s.points}</span>
                </div>
                {s.effect && s.effect.length > 0 && <div className="mt-1 text-[11px] text-muted-foreground">{s.effect[0]}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}
